import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { List, ListItem, InputLabel } from '@material-ui/core';
import FontPicker from 'font-picker-react';
import { useStoreState } from 'easy-peasy';
import { FastField } from 'formik';
import _e from 'common/localeUtil';
import {
	getFontFamilyFromHTML,
	getPrimaryColorForTemplate,
	removeGrapesJSComponents,
	setFontFamilyGrapesJS,
	setPrimaryColorForTemplate,
} from '../utils';
import EditorLeftPanelContent from './EditorLeftPanelContent';
import FieldWrapper from './form/FieldWrapper';
import LabelValueComponent from './LabelValueComponent';

const DEFAULT_FONT = 'Open Sans';

const useStyles = makeStyles((theme) => ({
	listItemStyle: {
		backgroundColor: theme.palette.primary[400],
		// opacity: 0.6,
		borderRadius: 4,
		cursor: 'pointer',
		transition: 'background 0.5s',
		'&:hover': {
			// zIndex: 0,
			backgroundColor: theme.palette.primary[300],
			// opacity: 0.8
		},
	},
	back: {
		fontSize: '1rem',
		marginBottom: 20,
		color: 'white',
		display: 'block',
	},
	line: {
		border: '1px solid #365275',
		width: '100%',
	},
	eachFontPicker: {
		display: 'flex',
		flex: 1,
		justifyContent: 'space-between',
		alignItems: 'center',
		// margin: '22px 0',

		'& .dropdown-button': {
			background: 'none !important',
			// border: '1px solid #365275'
			// boxShadow: '0 0 0 0.125em rgba(50, 115, 220, 0.25)'
		},
		'& .font-button:hover, & .active-font': {
			// background: '#478CCA !important'
		},

		'& .font-list': {
			background: '#141F2D',
		},
		'&. font-button': {
			'&::after': {
				content: '"A"',
			},
		},
		'& div[id=font-picker-header]': {
			'& .dropdown-font-family': {
				fontFamily: ({ headerFontFamily = DEFAULT_FONT }) => headerFontFamily,
			},
			'& .dropdown-icon.finished::before': {
				fontFamily: ({ headerFontFamily = DEFAULT_FONT }) => headerFontFamily,
			},
		},
		'& div[id=font-picker-body]': {
			'& .dropdown-font-family': {
				fontFamily: ({ bodyFontFamily = DEFAULT_FONT }) => bodyFontFamily,
			},
			'& .dropdown-icon.finished::before': {
				fontFamily: ({ bodyFontFamily = DEFAULT_FONT }) => bodyFontFamily,
			},
		},
		'& div[id^=font-picker]': {
			width: 'auto',
			minWidth: 120,
			boxShadow: 'none',
			'& .dropdown-icon.finished::before': {
				content: '"A"',
				transform: 'none',
				backgroundColor: '#FFF',
				color: '#000',
				padding: '4px 8px',
				border: 0,
				width: 26,
				height: 26,
				lineHeight: '18px',
				borderRadius: 2,
				// borderTopColor: 'white !important'
			},
		},
	},
}));

const getFontSelectors = (templateName) => {
	if (window.GM_BRANDING_TYPE_REPORT_EBOOK) {
		return {
			header: `#${templateName} .font-header`,
			body: `#${templateName}`,
		};
	}
	return {
		header: '.header',
		body: `.${templateName}`,
	};
};

const EditorLeftPanelStyle = (props) => {
	const [activeFontFamilyHeader, setActiveFontFamilyHeader] = useState();
	const [activeFontFamilyBody, setActiveFontFamilyBody] = useState();
	const adviserData = useStoreState((state) => state.template.adviserData);
	const template = useStoreState((state) => state.template.template);

	const classes = useStyles({
		headerFontFamily: activeFontFamilyHeader,
		bodyFontFamily: activeFontFamilyBody,
	});

	const setDefaultPrimaryColor = () => {
		const primaryColorHex = getPrimaryColorForTemplate(template);
		if (primaryColorHex) {
			props.setFieldValue('primaryColor', primaryColorHex);
		}
	};

	useEffect(() => {
		if (!window.GM_BRANDING_TYPE_REPORT_EBOOK) {
			setDefaultPrimaryColor();
		}

		const fontSelectors = getFontSelectors(template);

		const headerFontFamily = getFontFamilyFromHTML(fontSelectors.header, DEFAULT_FONT);
		if (headerFontFamily) {
			setTimeout(() => {
				setActiveFontFamilyHeader(headerFontFamily);
			}, 500);
		}
		const bodyFontFamily = getFontFamilyFromHTML(fontSelectors.body, DEFAULT_FONT);
		if (bodyFontFamily) {
			setTimeout(() => {
				setActiveFontFamilyBody(bodyFontFamily);
			}, 500);
		}
		console.log(fontSelectors, headerFontFamily, bodyFontFamily);
	}, []);

	const handleFontChange = (id, nextFont) => {
		window.gm_track(`Font changed`, { id, nextFont });

		// remove existing google fonts
		removeGrapesJSComponents(`link[href*="fonts.googleapis.com"]`);
		const fontSelectors = getFontSelectors(template);
		if (id === 'header') {
			setActiveFontFamilyHeader(nextFont.family);
		} else if (id === 'body') {
			setActiveFontFamilyBody(nextFont.family);
		}

		setFontFamilyGrapesJS(fontSelectors.header, activeFontFamilyHeader);
		setFontFamilyGrapesJS(fontSelectors.body, activeFontFamilyBody);
	};

	if (!adviserData?.id) {
		return false;
	}

	const handleColorChanged = (primaryOrSecondaryColor) => (hex) => {
		console.log('EditorLeftPanelStyle handleColorChanged', primaryOrSecondaryColor, hex);
		if (primaryOrSecondaryColor === 'primaryColor') {
			window.gm_track(`Primary color chosen`, { hex });

			setPrimaryColorForTemplate(template, hex);
		}
	};

	const content = (
		<div>
			<List>
				{!window.GM_BRANDING_TYPE_REPORT_EBOOK && (
					<ListItem classes={{ root: classes.listItemStyle }} divider>
						<LabelValueComponent
							noGutter
							label={<InputLabel>{_e('primary_color')}</InputLabel>}
							value={
								<FastField
									// defaultHex={`#123456`}
									name="primaryColor"
									component={FieldWrapper}
									control="colorpicker"
									callback={handleColorChanged('primaryColor')}
								/>
							}
						/>
					</ListItem>
				)}
				<ListItem classes={{ root: classes.listItemStyle }} divider>
					<div className={classes.eachFontPicker}>
						<div>{_e('header')}</div>
						<FontPicker
							apiKey={process.env.REACT_APP_GOOGLE_FONT_API_KEY}
							activeFontFamily={activeFontFamilyHeader}
							onChange={(nextFont) => handleFontChange('header', nextFont)}
							pickerId="header"
							className="header"
						/>
					</div>
				</ListItem>
				<ListItem classes={{ root: classes.listItemStyle }}>
					<div className={classes.eachFontPicker}>
						<div>{_e('body')}</div>
						<FontPicker
							apiKey={process.env.REACT_APP_GOOGLE_FONT_API_KEY}
							activeFontFamily={activeFontFamilyBody}
							onChange={(nextFont) => handleFontChange('body', nextFont)}
							pickerId="body"
							className="body"
						/>
					</div>
				</ListItem>
			</List>
		</div>
	);

	const { title, subtitle } = props;
	return <EditorLeftPanelContent title={title} subtitle={subtitle} content={content} />;
};

EditorLeftPanelStyle.defaultProps = {
	title: _e('styles'),
};

EditorLeftPanelStyle.propTypes = {
	setFieldValue: PropTypes.func,
	title: PropTypes.string,
	subtitle: PropTypes.string,
};
export default EditorLeftPanelStyle;
